/**********************************************************************
 * Copyright (C) 2023-2026 Red Hat, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * SPDX-License-Identifier: Apache-2.0
 ***********************************************************************/

import test, { expect as playExpect } from '@playwright/test';
import type { Locator, Page } from 'playwright';

import { Registries } from '/@/model/core/settings/registries';
import { handleConfirmationDialog } from '/@/utility/operations';
import { waitUntil } from '/@/utility/wait';

import { SettingsPage } from './settings-page';

export class RegistriesPage extends SettingsPage {
  readonly heading: Locator;
  readonly addRegistryButton: Locator;
  readonly registriesTable: Locator;
  readonly addRegistryDialog: Locator;
  readonly cancelDialogButton: Locator;
  readonly confirmDialogButton: Locator;
  readonly registryUrlField: Locator;
  readonly registryUsernameField: Locator;
  readonly registryPswdField: Locator;
  readonly preferredRepositoriesField: Locator;

  constructor(page: Page) {
    super(page, Registries.TABLE_ARIA_LABEL);
    this.heading = page.getByRole('heading').and(page.getByText(Registries.TABLE_ARIA_LABEL, { exact: true }));
    this.addRegistryButton = page.getByRole('button', { name: 'Add registry' });
    this.registriesTable = page.getByRole('table', { name: Registries.TABLE_ARIA_LABEL });
    this.addRegistryDialog = page.getByRole('dialog', { name: 'Add Registry' });
    this.cancelDialogButton = this.addRegistryDialog.getByRole('button', {
      name: 'Cancel',
    });
    this.confirmDialogButton = this.addRegistryDialog.getByRole('button', {
      name: 'Add',
    });
    this.registryUrlField = this.addRegistryDialog.getByPlaceholder('https://registry.io');
    this.registryUsernameField = this.addRegistryDialog.getByPlaceholder('username');
    this.registryPswdField = this.addRegistryDialog.getByPlaceholder('password');
    this.preferredRepositoriesField = page.locator(`input[name="${Registries.PREFERRED_INPUT_NAME}"]`);
  }

  async submitRegistryForm(url: string, username: string, pswd: string): Promise<void> {
    return test.step('Submit registry form', async () => {
      await this.page.waitForTimeout(4_000);
      await playExpect(this.addRegistryButton).toBeEnabled();
      await this.addRegistryButton.click();
      await playExpect(this.addRegistryDialog).toBeVisible();
      await playExpect(this.cancelDialogButton).toBeEnabled();

      await this.registryUrlField.fill(url);
      await this.registryUsernameField.fill(username);
      await this.registryPswdField.fill(pswd);

      await playExpect(this.confirmDialogButton).toBeEnabled();
      await this.confirmDialogButton.click();
    });
  }

  async createRegistry(url: string, username: string, pswd: string, handleUntrustedCert?: boolean): Promise<void> {
    return test.step('Create a new registry', async () => {
      await this.submitRegistryForm(url, username, pswd);

      if (handleUntrustedCert) {
        try {
          await handleConfirmationDialog(this.page, 'Add Untrusted Registry?', true, 'Add', 'Cancel', 5_000);
        } catch (err) {
          if ((err as Error).name !== 'TimeoutError' && !(err as Error).message?.includes('Timeout')) {
            throw err;
          }
        }
      }

      await playExpect(this.addRegistryDialog).toBeHidden({ timeout: 30_000 });
    });
  }

  async editRegistry(title: string, newUsername: string, newPswd: string): Promise<void> {
    return test.step('Edit registry', async () => {
      const registryBox = await this.getRegistryRowByName(title);

      const dropdownMenu = registryBox.getByRole('button', {
        name: 'kebab menu',
      });
      await dropdownMenu.click();

      const editButton = registryBox.getByTitle('Edit password');
      await editButton.click();

      const registryUsername = registryBox.getByLabel('Username');
      const registryPswd = registryBox.getByRole('textbox', {
        name: 'Password',
      });
      await registryUsername.pressSequentially(newUsername, { delay: 100 });
      await registryPswd.pressSequentially(newPswd, { delay: 100 });

      const loginButton = registryBox.getByRole('button', { name: 'Login' });
      await this.loginButtonHandling(loginButton);
    });
  }

  /*
   * There are two types of registries, if it is custom, then it can be actually deleted
   * If it is default registry, it will delete only the credentials and the record will be kept there.
   */
  async removeRegistry(title: string): Promise<void> {
    return test.step('Remove registry', async () => {
      const registryBox = await this.getRegistryRowByName(title);

      const dropdownMenu = registryBox.getByRole('button', {
        name: 'kebab menu',
      });
      try {
        await dropdownMenu.waitFor({ state: 'visible', timeout: 3_000 });
      } catch (_err) {
        throw Error(`Dropdown menu on ${title} registry not available.`);
      }
      await dropdownMenu.click();

      const editButton = registryBox.getByTitle('Remove');
      await editButton.click();
    });
  }

  async updatePreferredRepositories(repositories: string): Promise<void> {
    return test.step('Update preferred image repositories', async () => {
      await playExpect(this.preferredRepositoriesField).toBeVisible();

      await this.preferredRepositoriesField.clear();
      await playExpect(this.preferredRepositoriesField).toHaveValue('');

      await this.preferredRepositoriesField.fill(repositories);
      await playExpect(this.preferredRepositoriesField).toHaveValue(repositories);

      // wait for the preferred repositories field debounce timer to complete and modifications to be applied
      await this.page.waitForTimeout(2_000);
    });
  }

  async addPreferredRepositories(repositories: string[]): Promise<void> {
    return test.step('Add preferred image repositories', async () => {
      await playExpect(this.preferredRepositoriesField).toBeVisible();

      const current = await this.preferredRepositoriesField.inputValue();
      const existing = current
        ? current
            .split(',')
            .map(r => r.trim())
            .filter(Boolean)
        : [];
      const merged = [...repositories, ...existing].join(',');

      await this.preferredRepositoriesField.clear();
      await playExpect(this.preferredRepositoriesField).toHaveValue('');

      await this.preferredRepositoriesField.fill(merged);
      await playExpect(this.preferredRepositoriesField).toHaveValue(merged);

      // wait for the preferred repositories field debounce timer to complete and modifications to be applied
      await this.page.waitForTimeout(2_000);
    });
  }

  async getRegistryRowByName(name: string): Promise<Locator> {
    return this.registriesTable.getByRole('row', { name: name });
  }

  async isPreferredManaged(): Promise<boolean> {
    return test.step('Check if Preferred Repositories field has Managed label', async () => {
      try {
        // Find the section containing "Preferred" label text
        const preferredSection = this.page
          .locator('div')
          .filter({ hasText: Registries.Labels.PREFERRED })
          .filter({ has: this.preferredRepositoriesField });

        // Look for the "Managed" label within that section
        const managedLabel = preferredSection.getByText(Registries.Labels.MANAGED, { exact: true });
        await managedLabel.waitFor({ state: 'visible', timeout: 5_000 });
        return true;
      } catch (err) {
        // Only treat timeout errors as "not managed", re-throw other errors
        if ((err as Error).name === 'TimeoutError') {
          return false;
        }
        throw err;
      }
    });
  }

  private async loginButtonHandling(loginButton: Locator): Promise<void> {
    return test.step('Handle login button', async () => {
      try {
        await waitUntil(
          async function loginIsEnabled() {
            return await loginButton.isEnabled();
          },
          { message: 'Login Button not enabled in time' },
        );
        await loginButton.click({ timeout: 3000 });
      } catch (err) {
        throw Error(`An error occurred when trying to log into registry: ${(err as Error).message}`);
      }
    });
  }
}
